/**
 * The treasury dashboard — what we hold, what we owe, and whether the two agree.
 *
 * Read fresh on every open; nothing here is cached. The operator's whole reason for opening
 * this page is to decide whether there is enough money to keep converting, and a cached answer
 * to that question is worse than a slow one.
 *
 * `free` is the only figure a gate may spend against, so the table leads with it. A holding
 * whose `held` is null has never been read from the custodian successfully, and that is
 * rendered as a stated absence rather than as a dash: "we do not know" and "there is none" are
 * opposite operational situations, and a blank that reads as zero is how a screen tells an
 * operator everything is fine while a conversion gate is refusing every request.
 */
import { Link } from 'react-router-dom';

import { useAuth } from '../auth/AuthContext.js';
import type { TreasuryHolding } from '../contracts/types.js';
import { Badge, Card, ErrorBox, LoadingState } from '../components/ui.js';
import { IconAlert } from '../components/icons.js';
import { useAsync } from '../hooks/useAsync.js';
import { formatMoney } from '../lib/money.js';
import { relativeTime } from '../lib/format.js';

export function TreasuryOverviewPage(): JSX.Element {
  const { api } = useAuth();
  const state = useAsync(() => api.treasuryOverview(), []);

  if (state.loading) return <LoadingState label="Loading" />;
  if (state.error !== null) return <ErrorBox message={state.error} />;
  if (state.data === null) return <ErrorBox message="No treasury data." />;

  const o = state.data;

  return (
    <>
      <div className="page-head">
        <h1 className="page-head__title">Treasury</h1>
        <p className="page-head__sub">
          What you hold and what you owe, read fresh the moment you opened this page.
        </p>
      </div>

      {o.sandbox && (
        <div className="state-banner" role="status">
          <IconAlert size={18} />
          <div>
            <strong>Sandbox is on.</strong> Trades are written to your own records only, and your crypto provider is never contacted. Orders finish and balances move, but no coin is really bought or sold. Turn it off in <Link to="/treasury/thresholds">Trading limits</Link> when you are ready to trade for real.
          </div>
        </div>
      )}

      {o.conversionsPaused && (
        <div className="state-banner" role="status">
          <IconAlert size={18} />
          <div>
            <strong>Conversions are paused.</strong>{' '}
            {o.pausedReason === '' ? 'No reason was recorded.' : o.pausedReason}
            {o.pausedAt !== null && <> Paused {relativeTime(o.pausedAt)}.</>}
          </div>
        </div>
      )}

      <div className="grid grid--stats">
        <StatTile
          label="Naira available"
          value={formatMoney(o.ngnFloat)}
          meta={o.ngnFloatLow ? 'below the level you set as low' : 'what you can pay out from'}
          alert={o.ngnFloatLow}
        />
        <StatTile
          label="Needs your decision"
          value={String(o.openReviews)}
          meta={o.openReviews > 0 ? 'orders held for review' : 'nothing held'}
          alert={o.openReviews > 0}
          to="/treasury/review"
        />
        <StatTile
          label="Mismatches"
          value={String(o.openDeltas)}
          meta={
            o.lastReconciliationAt === null
              ? 'never checked'
              : `last checked ${relativeTime(o.lastReconciliationAt)}`
          }
          alert={o.openDeltas > 0}
          to="/treasury/reconciliation"
        />
      </div>

      <Card title="Holdings">
        <p className="muted" style={{ marginTop: 0 }}>
          <strong>Free</strong> is what you hold minus what you owe customers. It is the only amount a trade can actually spend.
        </p>
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Asset</th>
                <th className="num">Free</th>
                <th className="num">Held by your provider</th>
                <th className="num">Owed to customers</th>
                <th className="num">Your own record</th>
                <th>Last read</th>
              </tr>
            </thead>
            <tbody>
              {o.holdings.map((h) => (
                <HoldingRow key={h.currency} holding={h} />
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </>
  );
}

function HoldingRow({ holding: h }: { holding: TreasuryHolding }): JSX.Element {
  return (
    <tr>
      <td>
        <strong>{h.currency}</strong> {h.low && <Badge tone="amber">Low</Badge>}
      </td>
      <td className="num">
        <NotReported money={h.free} />
      </td>
      <td className="num">
        <NotReported money={h.held} />
      </td>
      <td className="num">{formatMoney(h.owed)}</td>
      <td className="num">{formatMoney(h.ledgerTreasury)}</td>
      <td>
        {h.fetchedAt === null ? (
          <Badge tone="red">never</Badge>
        ) : h.stale ? (
          <Badge tone="amber">too old · {relativeTime(h.fetchedAt)}</Badge>
        ) : (
          <span className="muted">{relativeTime(h.fetchedAt)}</span>
        )}
      </td>
    </tr>
  );
}

/**
 * A figure the custodian did not give us, said in words.
 *
 * Never a dash and never a zero. A gate refuses on an unreadable balance exactly as it refuses
 * on an empty one, but the operator's fix is completely different — one is a vendor to chase,
 * the other is money to move — and a cell that cannot tell them apart sends them the wrong way.
 */
function NotReported({ money }: { money: { currency: string; amount: string } | null }): JSX.Element {
  if (money === null) return <span className="muted">not reported</span>;
  return <>{formatMoney(money as Parameters<typeof formatMoney>[0])}</>;
}

function StatTile({
  label,
  value,
  meta,
  alert = false,
  to,
}: {
  label: string;
  value: string;
  meta: string;
  alert?: boolean;
  to?: string;
}): JSX.Element {
  const body = (
    <div className={alert ? 'stat stat--alert' : 'stat'}>
      <div className="stat__label">{label}</div>
      <div className="stat__value">{value}</div>
      <div className="stat__meta">{meta}</div>
    </div>
  );
  return to === undefined ? body : <Link to={to}>{body}</Link>;
}
